import { Conditional } from "@result/rui/components";
import { useInstanceId } from "@result/rui/hooks";
import {
  LABEL_TYPES,
  classNames,
  toClassName,
} from "@result/rui/utils/component";
import { t } from "i18next";
import PropTypes from "prop-types";
import { useRef, useState } from "react";

import {
  DATE_ACCURACY_DAY,
  DATE_ACCURACY_MONTH,
  canAutoNormalizeDateValueWithAccuracy,
  formatDateInputDisplayValue,
  formatDateInputDisplayValueByAccuracy,
  normalizeDateAccuracyValue,
  normalizeDateInputValue,
  normalizeDateValueWithAccuracy,
} from "#/utils/DateUtils";

const hiddenDateInputStyle = {
  position: "absolute",
  left: "0px",
  // top: "100%",
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: "none",
  border: 0,
  padding: 0,
  margin: 0,
  transform: "translateY(-50%)",
};

/**
 * Specialized date input component
 *
 * Requirements from PZI, 3.1.6.7:
 *    omogočen je vnos prek številčnice v poenostavljeni obliki
 *    d,m,rr ali d,m,rrrr, pri čemer sistem samodejno pretvori
 *    zapis v standardno obliko dd.mm.yyyy
 *    (npr. vnos 3,5,21 -> 03.05.2021 ali 3,5,1999 -> 03.05.1999).
 *
 * It allows manual input in standard + simplified form with normalization on
 * blur, while also keeping an underlying native date input for picker support.
 *
 * The component stores dates in ISO format (yyyy-MM-dd). Depending on
 * accuracy it displays a full date (dd.MM.yyyy), month + year (MM.yyyy), or
 * year only (yyyy).
 */
export default function DateInput({
  id,
  label,
  labelType = "inline",
  name,
  placeholder = "dd.mm.yyyy",
  helpText,
  isDisabled,
  isRequired: required,
  onChange,
  onFocus,
  onBlur,
  className,
  value,
  formField,
  formFieldAccuracy,
  iconLeft,
  iconRight,
  onIconRightClick,
  onIconLeftClick,
  min,
  max,
  ...props
}) {
  const instanceId = useInstanceId(id, "date-input");
  const fieldName = formField ? formField.id : name;
  const isRequired =
    required == null ? (formField?.isRequired ?? false) : required;
  const externalValue = formField ? formField.value : value;
  const externalAccuracy = normalizeDateAccuracyValue(formFieldAccuracy?.value);
  const normalizedExternalValue = normalizeDateInputValue(externalValue);
  const [inputState, setInputState] = useState(() => ({
    sourceValue: normalizedExternalValue,
    sourceAccuracy: externalAccuracy,
    displayValue: formatDateInputDisplayValueByAccuracy(
      externalValue,
      externalAccuracy
    ),
    localValidationError: null,
  }));
  const nativeDateInputRef = useRef(null);

  if (inputState.sourceValue !== normalizedExternalValue) {
    setInputState({
      sourceValue: normalizedExternalValue,
      displayValue: formatDateInputDisplayValue(externalValue),
      localValidationError: null,
    });
  }

  const isSyncedWithExternalValue =
    inputState.sourceValue === normalizedExternalValue &&
    inputState.sourceAccuracy === externalAccuracy;
  const displayValue = isSyncedWithExternalValue
    ? inputState.displayValue
    : formatDateInputDisplayValueByAccuracy(externalValue, externalAccuracy);
  const localValidationError = isSyncedWithExternalValue
    ? inputState.localValidationError
    : null;

  const updateInputState = (nextDisplayValue, nextError = null) => {
    setInputState({
      sourceValue: normalizedExternalValue,
      sourceAccuracy: externalAccuracy,
      displayValue: nextDisplayValue,
      localValidationError: nextError,
    });
  };

  const updateValue = (
    nextValue,
    nextAccuracy,
    { notify = true, validate = false } = {}
  ) => {
    formField?.setValue(nextValue);
    formFieldAccuracy?.setValue(nextValue ? nextAccuracy : null);

    if (notify) {
      onChange?.(nextValue, nextValue ? nextAccuracy : null);
    }

    if (validate) {
      formField?.validate?.();
      formFieldAccuracy?.validate?.();
    } else if (formField?.error) {
      formField?.validate?.();
    }

    if (!validate && formFieldAccuracy?.error) {
      formFieldAccuracy.validate?.();
    }
  };

  const openNativePicker = () => {
    if (isDisabled || !nativeDateInputRef.current) {
      return;
    }

    const nativeInput = nativeDateInputRef.current;

    if (typeof nativeInput.showPicker === "function") {
      try {
        nativeInput.showPicker();
        return;
      } catch {
        // Fall back to focus for browsers that reject programmatic showPicker.
      }
    }

    nativeInput.focus({ preventScroll: true });
  };

  const handleTextChange = (event) => {
    const nextDisplayValue = event.target.value;

    updateInputState(nextDisplayValue);

    if (nextDisplayValue.trim() === "") {
      updateValue(null, null);
      return;
    }

    const normalizedValue = normalizeDateValueWithAccuracy(nextDisplayValue);
    if (
      normalizedValue &&
      canAutoNormalizeDateValueWithAccuracy(nextDisplayValue)
    ) {
      updateValue(normalizedValue.normalizedValue, normalizedValue.accuracy);
    }
  };

  const handleTextFocus = (event) => {
    onFocus?.(event);
  };

  const handleTextBlur = (event) => {
    const trimmedValue = displayValue.trim();

    if (trimmedValue === "") {
      updateInputState("");
      updateValue(null, null, { notify: false, validate: true });
      onBlur?.(event);
      return;
    }

    const normalizedValue = normalizeDateValueWithAccuracy(trimmedValue);
    if (normalizedValue) {
      updateInputState(
        formatDateInputDisplayValueByAccuracy(
          normalizedValue.normalizedValue,
          normalizedValue.accuracy
        )
      );
      updateValue(normalizedValue.normalizedValue, normalizedValue.accuracy, {
        notify: false,
        validate: true,
      });
      onBlur?.(event);
      return;
    }

    updateInputState(trimmedValue, t("form.invalidDate", "Neveljaven datum"));
    formField?.validate();
    onBlur?.(event);
  };

  const handleNativeDateChange = (event) => {
    const nextValue = event.target.value || null;
    const normalizedValue =
      event.target.type === "month" && nextValue
        ? `${nextValue}-01`
        : nextValue;
    const nextAccuracy =
      event.target.type === "month" ? DATE_ACCURACY_MONTH : DATE_ACCURACY_DAY;

    updateInputState(
      formatDateInputDisplayValueByAccuracy(normalizedValue, nextAccuracy)
    );
    updateValue(normalizedValue, nextAccuracy);
  };

  const effectiveIconRight = iconRight ?? "icon-calendar";
  const effectiveOnIconRightClick = onIconRightClick ?? openNativePicker;
  const hasRightIcon = !!effectiveIconRight;
  const validationError = localValidationError || formField?.error;
  const nativeInputType =
    externalAccuracy === DATE_ACCURACY_MONTH ? "month" : "date";
  const nativeInputValue =
    nativeInputType === "month"
      ? normalizedExternalValue?.slice(0, 7) || ""
      : normalizedExternalValue || "";
  const nativeInputMin =
    nativeInputType === "month" && typeof min === "string"
      ? min.slice(0, 7)
      : min;
  const nativeInputMax =
    nativeInputType === "month" && typeof max === "string"
      ? max.slice(0, 7)
      : max;

  return (
    <div className={classNames("field", LABEL_TYPES[labelType])}>
      <Conditional condition={label}>
        <label htmlFor={instanceId} className="label">
          {label}
          <Conditional condition={isRequired}>
            <span className="has-text-danger is-unselectable"> *</span>
          </Conditional>
        </label>
      </Conditional>

      <div
        className={classNames(
          "control",
          toClassName(!!iconLeft, "has-icons-left"),
          toClassName(hasRightIcon, "has-icons-right"),
          className
        )}>
        <Conditional condition={label}>
          <label htmlFor={instanceId} className="label is-floating">
            {label}
            <Conditional condition={isRequired}>
              <span className="has-text-danger is-unselectable"> *</span>
            </Conditional>
          </label>
        </Conditional>

        <input
          {...props}
          id={instanceId}
          name={fieldName}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder={placeholder}
          disabled={isDisabled}
          className={classNames(
            "input",
            toClassName(!!validationError, "is-danger")
          )}
          value={displayValue}
          onChange={handleTextChange}
          onFocus={handleTextFocus}
          onBlur={handleTextBlur}
          aria-invalid={!!validationError}
        />
        <input
          ref={nativeDateInputRef}
          type={nativeInputType}
          tabIndex={-1}
          aria-hidden="true"
          disabled={isDisabled}
          value={nativeInputValue}
          min={nativeInputMin}
          max={nativeInputMax}
          onChange={handleNativeDateChange}
          style={hiddenDateInputStyle}
        />

        <Conditional condition={!!iconLeft}>
          <button
            type="button"
            className={classNames(
              "icon is-small is-left",
              toClassName(!!onIconLeftClick, "is-clickable")
            )}
            onClick={onIconLeftClick}
            tabIndex={onIconLeftClick ? 0 : -1}>
            <i className={iconLeft} />
          </button>
        </Conditional>
        <Conditional condition={hasRightIcon}>
          <button
            type="button"
            className={classNames(
              "icon is-small is-right",
              toClassName(hasRightIcon, "is-clickable")
            )}
            onClick={effectiveOnIconRightClick}
            tabIndex={hasRightIcon ? 0 : -1}>
            <i className={effectiveIconRight} />
          </button>
        </Conditional>
      </div>

      <Conditional condition={validationError}>
        <p className="help is-danger">{validationError}</p>
      </Conditional>
      <Conditional condition={!validationError && helpText}>
        <p className="help">{helpText}</p>
      </Conditional>
    </div>
  );
}

DateInput.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  labelType: PropTypes.oneOf(["floating", "inline"]),
  name: PropTypes.string,
  placeholder: PropTypes.string,
  helpText: PropTypes.string,
  isRequired: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  className: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  formField: PropTypes.shape({
    id: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    setValue: PropTypes.func,
    error: PropTypes.string,
    validate: PropTypes.func,
    isRequired: PropTypes.bool,
  }),
  formFieldAccuracy: PropTypes.shape({
    id: PropTypes.string,
    value: PropTypes.string,
    setValue: PropTypes.func,
    error: PropTypes.string,
    validate: PropTypes.func,
    isRequired: PropTypes.bool,
  }),
  iconLeft: PropTypes.string,
  iconRight: PropTypes.string,
  onIconRightClick: PropTypes.func,
  onIconLeftClick: PropTypes.func,
  min: PropTypes.string,
  max: PropTypes.string,
};
